import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, LogOut, ArrowLeft, Inbox, Mail, MailOpen, Clock, CheckCircle, AlertCircle, Send, User, Briefcase, Filter, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

import { BACKEND_URL } from '../config/api';

const STATUS_COLORS = {
  open: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-blue-100 text-blue-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-slate-100 text-slate-600'
};

const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed'
};

const CATEGORY_LABELS = {
  general: 'General',
  billing: 'Billing',
  technical: 'Technical',
  feedback: 'Feedback',
  report: 'Report Issue'
};

const USER_TYPE_ICONS = {
  employer: Briefcase,
  tradesperson: User,
  admin: Building2
};

export default function AdminSupportInbox() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({});
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) {
      fetchTickets();
    }
  }, [user, filterStatus, filterCategory]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      
      if (response.data.user_type !== 'admin') {
        navigate('/login');
        return;
      }
      
      setUser(response.data);
    } catch (error) {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchTickets = async () => {
    try {
      let url = `${BACKEND_URL}/api/admin/support-inbox`;
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterCategory) params.append('category', filterCategory);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, { withCredentials: true });
      setTickets(response.data.tickets || []);
      setCounts(response.data.counts || {});
    } catch (error) {
      toast.error('Failed to fetch tickets');
    }
  };

  const openTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setReplyText('');
    
    // Mark as read
    if (!ticket.read_by_admin) {
      try {
        await axios.put(
          `${BACKEND_URL}/api/admin/support-ticket/${ticket.ticket_id}/read`,
          {},
          { withCredentials: true }
        );
        fetchTickets();
      } catch (error) {
        console.error('Failed to mark as read');
      }
    }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    
    setSending(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/support/ticket/${selectedTicket.ticket_id}/reply`,
        { message: replyText },
        { withCredentials: true }
      );
      
      toast.success('Reply sent');
      setReplyText('');
      
      // Refresh ticket
      const response = await axios.get(
        `${BACKEND_URL}/api/support/ticket/${selectedTicket.ticket_id}`,
        { withCredentials: true }
      );
      setSelectedTicket(response.data.ticket);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const updateStatus = async (status) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/support-ticket/${selectedTicket.ticket_id}/status?status=${status}`,
        {},
        { withCredentials: true }
      );
      
      toast.success(`Status updated to ${STATUS_LABELS[status]}`);
      setSelectedTicket({ ...selectedTicket, status });
      fetchTickets();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-support-inbox">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              <Inbox className="h-6 w-6 text-orange-500" />
              <h1 className="text-xl font-bold text-slate-900">Support Inbox</h1>
              {counts.unread > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {counts.unread} new
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">Hi, {user?.name}</span>
            <button
              onClick={handleLogout}
              className="btn-secondary text-sm flex items-center gap-2"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="card text-center">
            <p className="text-2xl font-bold text-slate-900">{counts.total || 0}</p>
            <p className="text-sm text-slate-500">Total</p>
          </div>
          <div className="card text-center border-yellow-200 bg-yellow-50">
            <p className="text-2xl font-bold text-yellow-700">{counts.open || 0}</p>
            <p className="text-sm text-yellow-600">Open</p>
          </div>
          <div className="card text-center border-blue-200 bg-blue-50">
            <p className="text-2xl font-bold text-blue-700">{counts.in_progress || 0}</p>
            <p className="text-sm text-blue-600">In Progress</p>
          </div>
          <div className="card text-center border-green-200 bg-green-50">
            <p className="text-2xl font-bold text-green-700">{counts.resolved || 0}</p>
            <p className="text-sm text-green-600">Resolved</p>
          </div>
          <div className="card text-center border-red-200 bg-red-50">
            <p className="text-2xl font-bold text-red-700">{counts.unread || 0}</p>
            <p className="text-sm text-red-600">Unread</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="text-sm"
              data-testid="filter-status"
            >
              <option value="">All Statuses</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="text-sm"
            data-testid="filter-category"
          >
            <option value="">All Categories</option>
            <option value="general">General</option>
            <option value="billing">Billing</option>
            <option value="technical">Technical</option>
            <option value="feedback">Feedback</option>
            <option value="report">Report Issue</option>
          </select>
        </div>

        {/* Tickets List */}
        {tickets.length === 0 ? (
          <div className="card text-center py-12">
            <Inbox className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No support tickets</p>
            <p className="text-sm text-slate-400 mt-1">Messages from users will appear here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tickets.map(ticket => {
              const UserIcon = USER_TYPE_ICONS[ticket.user_type] || User;
              return (
                <div
                  key={ticket.ticket_id}
                  onClick={() => openTicket(ticket)}
                  className={`card cursor-pointer hover:border-orange-300 transition-colors ${
                    !ticket.read_by_admin ? 'border-l-4 border-l-orange-500 bg-orange-50/50' : ''
                  }`}
                  data-testid={`ticket-${ticket.ticket_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-full ${!ticket.read_by_admin ? 'bg-orange-100' : 'bg-slate-100'}`}>
                        {!ticket.read_by_admin ? (
                          <Mail className="h-5 w-5 text-orange-600" />
                        ) : (
                          <MailOpen className="h-5 w-5 text-slate-400" />
                        )}
                      </div>
                      <div>
                        <h3 className={`font-semibold ${!ticket.read_by_admin ? 'text-slate-900' : 'text-slate-700'}`}>
                          {ticket.subject}
                        </h3>
                        <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                          <UserIcon className="h-3 w-3" />
                          <span>{ticket.user_name}</span>
                          <span>•</span>
                          <span className="capitalize">{ticket.user_type}</span>
                          <span>•</span>
                          <span>{ticket.user_email}</span>
                        </div>
                        <p className="text-sm text-slate-600 mt-2 line-clamp-1">
                          {ticket.messages[0]?.content}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[ticket.status]}`}>
                        {STATUS_LABELS[ticket.status]}
                      </span>
                      <p className="text-xs text-slate-400 mt-2">
                        {formatDate(ticket.updated_at)}
                      </p>
                      <p className="text-xs text-slate-400">
                        {ticket.messages.length} message{ticket.messages.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Ticket Detail Modal */}
      <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-orange-500" />
                {selectedTicket?.subject}
              </span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[selectedTicket?.status]}`}>
                {STATUS_LABELS[selectedTicket?.status]}
              </span>
            </DialogTitle>
          </DialogHeader>

          {selectedTicket && (
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Ticket Info */}
              <div className="bg-slate-50 rounded-lg p-3 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <div>
                    <span className="font-medium text-slate-900">{selectedTicket.user_name}</span>
                    <span className="text-slate-500 ml-2">({selectedTicket.user_email})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 capitalize">{selectedTicket.user_type}</span>
                    <span className="px-2 py-0.5 bg-slate-200 text-slate-600 rounded text-xs">
                      {CATEGORY_LABELS[selectedTicket.category] || selectedTicket.category}
                    </span>
                  </div>
                </div>
              </div>

              {/* Status Actions */}
              <div className="flex gap-2 mb-4">
                <span className="text-sm text-slate-500">Set status:</span>
                {['open', 'in_progress', 'resolved', 'closed'].map(status => (
                  <button
                    key={status}
                    onClick={() => updateStatus(status)}
                    className={`px-2 py-1 rounded text-xs transition-colors ${
                      selectedTicket.status === status
                        ? STATUS_COLORS[status]
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {STATUS_LABELS[status]}
                  </button>
                ))}
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto space-y-3 mb-4 max-h-[300px]">
                {selectedTicket.messages.map((msg, idx) => (
                  <div
                    key={msg.message_id || idx}
                    className={`p-3 rounded-lg ${
                      msg.sender === 'admin'
                        ? 'bg-orange-50 border border-orange-200 ml-8'
                        : 'bg-slate-100 mr-8'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-sm font-medium ${
                        msg.sender === 'admin' ? 'text-orange-700' : 'text-slate-700'
                      }`}>
                        {msg.sender === 'admin' ? '🛡️ Support Team' : msg.sender_name}
                      </span>
                      <span className="text-xs text-slate-400">
                        {formatDate(msg.created_at)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{msg.content}</p>
                  </div>
                ))}
              </div>

              {/* Reply Form */}
              <div className="border-t pt-4">
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type your reply..."
                  rows={3}
                  className="w-full text-sm mb-2"
                  data-testid="reply-input"
                />
                <div className="flex justify-end">
                  <button
                    onClick={sendReply}
                    disabled={!replyText.trim() || sending}
                    className="btn-primary flex items-center gap-2"
                    data-testid="send-reply-btn"
                  >
                    <Send className="h-4 w-4" />
                    {sending ? 'Sending...' : 'Send Reply'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
